import { inject, Injectable } from '@angular/core';

import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, forkJoin, from, map, of, switchMap } from 'rxjs';

import { DataRepository } from '@core/data/data-repository';
import { OpportunitiesApiActions, OpportunitiesPageActions } from './opportunities.actions';

/**
 * Bridges opportunities actions to the data layer. The effect loads the funnel
 * opportunities and stages together; edits stay in the reducer (session-only).
 */
@Injectable()
export class OpportunitiesEffects {
  private readonly actions$ = inject(Actions);
  private readonly repo = inject(DataRepository);

  /** On page enter, load opportunities and stages in parallel via forkJoin. */
  readonly load$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OpportunitiesPageActions.entered),
      switchMap(() =>
        forkJoin({
          opportunities: from(this.repo.listOpportunities()),
          stages: from(this.repo.listOppStages()),
        }).pipe(
          map(({ opportunities, stages }) => OpportunitiesApiActions.loadSuccess({ opportunities, stages })),
          catchError((err: unknown) =>
            of(OpportunitiesApiActions.loadFailure({ error: toMessage(err) })),
          ),
        ),
      ),
    ),
  );
}

function toMessage(err: unknown): string {
  return err instanceof Error ? err.message : 'Unknown error';
}
